import { ReduxState, TwoFaQrCode } from 'interfaces';
import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { Button, Modal, OtpInput } from '../../components';
import { ConfigProvider, Divider, QRCode, Steps } from 'antd';
import { MdCheckCircle, MdCloudDownload, MdLockReset, MdQrCode } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { ReactComponent as GooglePlayIcon } from 'assets/icons/security/GooglePlay.svg';
import { ReactComponent as AndroidIcon } from 'assets/icons/security/Android.svg';
import { ReactComponent as PWAIcon } from 'assets/icons/security/PWA.svg';
import { disable2Fa, getProfile, setup2Fa, twoFaQrCode } from '../../../services';
import { TbCopy } from 'react-icons/tb';
import { copyToClipboard } from '../../../scripts';
import { connect, ConnectedProps } from 'react-redux';
import { toast } from 'react-toastify';

interface OwnProps {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    isActive?: boolean;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

enum Stage {
    DownloadApplication = 0,
    QRCode = 1,
    RecoveryCode = 2,
    Activation = 3,
}

const { Step } = Steps;

const GoogleAuthenticationModal: FC<Props> = (props) => {
    const [stage, setStage] = useState<Stage>(Stage.DownloadApplication);
    const [loading, setLoading] = useState<boolean>(false);
    const [qrCode, setQrCode] = useState<TwoFaQrCode>();
    const [code, setCode] = useState<string>('');

    const steps = [
        {
            title: 'دانلود اپلیکیشن',
            icon: (
                <MdCloudDownload
                    size={20}
                    className={`${stage >= Stage.DownloadApplication ? 'text-primary-100' : 'text-black-100'}`}
                />
            ),
        },
        {
            title: 'اسکن QR کد',
            icon: <MdQrCode size={20} className={`${stage >= Stage.QRCode ? 'text-primary-100' : 'text-black-100'}`} />,
        },
        {
            title: 'کد بازیابی',
            icon: (
                <MdLockReset
                    size={20}
                    className={`${stage >= Stage.RecoveryCode ? 'text-primary-100' : 'text-black-100'}`}
                />
            ),
        },
        {
            title: 'فعالسازی',
            icon: (
                <MdCheckCircle
                    size={20}
                    className={`${stage >= Stage.Activation ? 'text-primary-100' : 'text-black-100'}`}
                />
            ),
        },
    ];

    const nextStep = () => {
        if (stage < Stage.Activation) {
            setStage(stage + 1);
        }
    };

    const prevStep = () => {
        if (stage > Stage.DownloadApplication) {
            setStage(stage - 1);
        }
    };

    const getQrCode = () => {
        twoFaQrCode(setLoading, setQrCode, nextStep);
    };

    const onSetup2Fa = () => {
        const onComplete = () => {
            getProfile(props.dispatch).then(() => {
                toast.success('شناسایی دو عاملی با موفقیت فعال شد.');
                setLoading(false);
                props.setOpen(false);
            });
        };
        setup2Fa(setLoading, { code: code, type: 2 }, onComplete);
    };

    const onDisable2Fa = () => {
        const onComplete = () => {
            getProfile(props.dispatch).then(() => {
                toast.success('شناسایی دو عاملی با موفقیت غیر فعال شد.');
                setLoading(false);
                props.setOpen(false);
            });
        };

        disable2Fa(setLoading, { code: code }, onComplete);
    };

    useEffect(() => {
        if (!props.open) {
            setStage(Stage.DownloadApplication);
            setQrCode(undefined);
            setCode('');
        }
    }, [props.open]);

    return (
        <Modal
            open={props.open}
            setOpen={props.setOpen}
            title={'شناسایی دو عاملی'}
            width={'max-content'}
            className="lg:min-w-96"
        >
            <Divider />
            {!props.isActive ? (
                <>
                    <ConfigProvider
                        theme={{
                            components: {
                                Steps: {
                                    iconSize: 20,
                                },
                            },
                        }}
                    >
                        <Steps current={stage} labelPlacement={'vertical'}>
                            {steps.map((step, index) => (
                                <Step
                                    key={index}
                                    status={stage >= index ? 'process' : 'wait'}
                                    icon={step.icon}
                                    title={
                                        <span
                                            className={`font-bold text-sm ${stage >= index ? 'text-primary-100' : 'text-black-100'}`}
                                        >
                                            {step.title}
                                        </span>
                                    }
                                    active
                                />
                            ))}
                        </Steps>
                    </ConfigProvider>

                    {stage === Stage.DownloadApplication && (
                        <>
                            <p className={'font-bold text-xl text-primary-100 mt-10 text-center'}>مرحله اول</p>
                            <p className={'font-bold text-base text-black-100 mt-4 text-center'}>
                                اپلیکیشن <span className={'en'}>Google Authenticator</span> را دانلود و نصب کنید
                            </p>
                            <div className={'flex items-center gap-4 mt-5'}>
                                <Link
                                    to={''}
                                    className={'flex items-center gap-3 h-10 rounded-3xl border border-primary-20 p-1'}
                                >
                                    <div
                                        className={
                                            'flex items-center justify-center w-8 h-8 bg-gradient-to-t from-primaryGradient-1 to-primaryGradient-2 rounded-full'
                                        }
                                    >
                                        <GooglePlayIcon />
                                    </div>
                                    <span className={'font-normal text-base text-black-100 ml-3'}>گوگل پلی</span>
                                </Link>
                                <Link
                                    to={''}
                                    className={'flex items-center gap-3 h-10 rounded-3xl border border-primary-20 p-1'}
                                >
                                    <div
                                        className={
                                            'flex items-center justify-center w-8 h-8 bg-gradient-to-t from-primaryGradient-1 to-primaryGradient-2 rounded-full'
                                        }
                                    >
                                        <AndroidIcon />
                                    </div>
                                    <span className={'font-normal text-base text-black-100 ml-3'}>دریافت مستقیم</span>
                                </Link>
                                <Link
                                    to={''}
                                    className={'flex items-center gap-3 h-10 rounded-3xl border border-primary-20 p-1'}
                                >
                                    <div
                                        className={
                                            'flex items-center justify-center w-8 h-8 bg-gradient-to-t from-primaryGradient-1 to-primaryGradient-2 rounded-full'
                                        }
                                    >
                                        <PWAIcon />
                                    </div>
                                    <span className={'font-normal text-base text-black-100 ml-3'}>وب اپلیکیشن</span>
                                </Link>
                            </div>
                            <div className={'flex items-center gap-4 mt-8'}>
                                <Button
                                    type={'primary'}
                                    className={'w-full'}
                                    onClick={getQrCode}
                                    loading={loading}
                                    disabled={loading}
                                >
                                    مرحله بعد
                                </Button>
                                <Button
                                    type={'text'}
                                    className={'bg-background w-full'}
                                    onClick={() => props.setOpen(false)}
                                >
                                    انصراف
                                </Button>
                            </div>
                        </>
                    )}
                    {stage === Stage.QRCode && (
                        <>
                            <p className={'font-bold text-xl text-primary-100 mt-10 text-center'}>مرحله دوم</p>
                            <p className={'font-bold text-base text-black-100 mt-4 text-center'}>
                                لطفا بارکد را با تلفن همراه در نرم افزار Google Authenticator اسکن کنید.
                                <br />
                                اگر امکان اسکن توسط گوشی برای شما مهیا نیست، می‌توانید کد زیر را به صورت دستی در آن وارد
                                نمایید.
                            </p>
                            <div className={'flex mt-6 rounded-lg border border-primary-20 p-4 gap-6'}>
                                <QRCode value={qrCode?.qrCodeUrl ?? ''} bordered={false} />
                                <div className={'flex flex-col justify-end gap-2'}>
                                    <label className={'font-bold text-base'}>کد شناسایی دو عاملی:</label>
                                    <div
                                        className={
                                            'flex items-center justify-between h-12 rounded-[28px] border border-primary-20 gap-4 px-3'
                                        }
                                    >
                                        <span className={'text-base font-normal en whitespace-nowrap'}>
                                            {qrCode?.authenticatorKey}
                                        </span>
                                        <Button
                                            type={'primary'}
                                            icon={<TbCopy />}
                                            className={'h-8 rounded-[32px]'}
                                            onClick={() =>
                                                copyToClipboard(qrCode?.authenticatorKey, 'کد شناسایی دو عاملی')
                                            }
                                        >
                                            کپی
                                        </Button>
                                    </div>
                                </div>
                            </div>
                            <div className={'flex items-center gap-4 mt-8'}>
                                <Button type={'primary'} className={'w-full'} onClick={nextStep}>
                                    مرحله بعد
                                </Button>
                                <Button
                                    type={'text'}
                                    className={'bg-background w-full'}
                                    onClick={() => props.setOpen(false)}
                                >
                                    انصراف
                                </Button>
                            </div>
                        </>
                    )}
                    {stage === Stage.RecoveryCode && (
                        <>
                            <p className={'font-bold text-xl text-primary-100 mt-10 text-center'}>مرحله سوم</p>
                            <p className={'font-bold text-base text-black-100 mt-4 text-center'}>
                                این کد برای بازیابی حساب شما در اپلیکیشن Google Authenticator در صورت گم شدن تلفن همراه
                                است.
                                <br />
                                لطفا کد زیر را بر روی کاغذ بنویسید.
                            </p>
                            <div
                                className={
                                    'flex items-center justify-between h-12 rounded-[28px] border border-primary-20 gap-4 px-3 mt-6'
                                }
                            >
                                <span className={'text-base font-normal en whitespace-nowrap'}>
                                    {qrCode?.recoveryCodes}
                                </span>
                                <Button
                                    type={'primary'}
                                    icon={<TbCopy />}
                                    className={'h-8 rounded-[32px]'}
                                    onClick={() => copyToClipboard(qrCode?.recoveryCodes.join(''), 'کد بازیابی')}
                                >
                                    کپی
                                </Button>
                            </div>
                            <div className={'flex items-center gap-4 mt-8'}>
                                <Button type={'primary'} className={'w-full'} onClick={nextStep}>
                                    مرحله بعد
                                </Button>
                                <Button
                                    type={'text'}
                                    className={'bg-background w-full'}
                                    onClick={() => props.setOpen(false)}
                                >
                                    انصراف
                                </Button>
                            </div>
                        </>
                    )}
                    {stage === Stage.Activation && (
                        <>
                            <p className={'font-bold text-xl text-primary-100 mt-10 text-center'}>مرحله چهارم</p>
                            <p className={'font-bold text-base text-black-100 mt-4 text-center'}>
                                فعال‌سازی Google Authenticator
                            </p>
                            <label className={'flex mt-6 font-bold text-sm'}>کد دو مرحله‌ای Google Authenticator</label>
                            <OtpInput length={6} onChange={(code) => setCode(code)} className={'mt-2'} />
                            <div className={'flex items-center gap-4 mt-8'}>
                                <Button
                                    type={'primary'}
                                    className={'w-full'}
                                    onClick={onSetup2Fa}
                                    loading={loading}
                                    disabled={loading || code.length < 6}
                                >
                                    فعال سازی
                                </Button>
                                <Button
                                    type={'text'}
                                    className={'bg-background w-full'}
                                    onClick={() => props.setOpen(false)}
                                >
                                    انصراف
                                </Button>
                            </div>
                        </>
                    )}
                </>
            ) : (
                <>
                    <p className={'font-bold text-base text-black-100 mt-4 text-center'}>
                        غیر فعال‌سازی Google Authenticator
                    </p>
                    <label className={'flex mt-6 font-bold text-sm'}>کد دو مرحله‌ای Google Authenticator</label>
                    <OtpInput length={6} onChange={(code) => setCode(code)} className={'mt-2'} />
                    <div className={'flex items-center gap-4 mt-8'}>
                        <Button
                            type={'primary'}
                            className={'w-full'}
                            onClick={onDisable2Fa}
                            loading={loading}
                            disabled={loading || code.length < 6}
                        >
                            غیر فعال سازی
                        </Button>
                        <Button type={'text'} className={'bg-background w-full'} onClick={() => props.setOpen(false)}>
                            انصراف
                        </Button>
                    </div>
                </>
            )}
        </Modal>
    );
};

const mapStateToProps = (state: ReduxState) => ({
    isAuth: state.authStatus,
    user: state.userData,
});

const connector = connect(mapStateToProps);
export default connector(GoogleAuthenticationModal);
